import { isRecord } from "@arti/utilities";

import {
  PROFILE_SECTION_DEFINITIONS,
  type SectionRowDefinition,
} from "./repositoryProfileMetadata.config";

export interface DetailRow {
  label: string;
  values: string[];
}

export interface DetailSection {
  title: string;
  rows: DetailRow[];
}

function getRecord(
  input: Record<string, unknown> | null,
  path: readonly string[],
): Record<string, unknown> | null {
  let current: unknown = input;
  for (const segment of path) {
    if (!isRecord(current)) {
      return null;
    }
    current = current[segment];
  }
  return isRecord(current) ? current : null;
}

export function resolveProfileMetadata(
  metadata: Record<string, unknown> | null,
): Record<string, unknown> | null {
  if (!metadata) {
    return null;
  }
  const candidate = metadata.profile;
  return isRecord(candidate) ? candidate : metadata;
}

function toStringValue(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function dedupe(values: string[]): string[] {
  return Array.from(new Set(values));
}

function toStringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const normalized = value
    .map((entry) => toStringValue(entry))
    .filter((entry): entry is string => Boolean(entry));
  return dedupe(normalized);
}

export function readString(
  input: Record<string, unknown> | null,
  path: readonly string[],
): string | null {
  if (!input || path.length === 0) {
    return null;
  }
  const parent = getRecord(input, path.slice(0, -1));
  if (!parent) {
    return null;
  }
  return toStringValue(parent[path[path.length - 1]]);
}

function readStringList(
  input: Record<string, unknown> | null,
  path: readonly string[],
): string[] {
  if (!input || path.length === 0) {
    return [];
  }
  const parent = getRecord(input, path.slice(0, -1));
  if (!parent) {
    return [];
  }
  return toStringList(parent[path[path.length - 1]]);
}

function readBranchList(
  input: Record<string, unknown> | null,
  path: readonly string[],
): string[] {
  if (!input || path.length === 0) {
    return [];
  }
  const parent = getRecord(input, path.slice(0, -1));
  if (!parent) {
    return [];
  }
  const candidate = parent[path[path.length - 1]];
  if (!Array.isArray(candidate)) {
    return [];
  }

  const branches: string[] = [];
  for (const entry of candidate) {
    if (!isRecord(entry)) {
      continue;
    }
    const name = toStringValue(entry.name);
    if (!name) {
      continue;
    }
    const description = toStringValue(entry.description);
    branches.push(description ? `${name} (${description})` : name);
  }
  return dedupe(branches);
}

function readRowValues(
  metadata: Record<string, unknown>,
  row: SectionRowDefinition,
): string[] {
  if (row.mode === "branches" && row.path) {
    return readBranchList(metadata, row.path);
  }
  if (row.mode === "merged_lists" && row.paths) {
    return dedupe(row.paths.flatMap((path) => readStringList(metadata, path)));
  }
  if (row.path) {
    return readStringList(metadata, row.path);
  }
  return [];
}

export function createSections(
  metadata: Record<string, unknown> | null,
): DetailSection[] {
  if (!metadata) {
    return [];
  }

  return PROFILE_SECTION_DEFINITIONS.map((section) => ({
    rows: section.rows
      .map((row) => ({
        label: row.label,
        values: dedupe(readRowValues(metadata, row)),
      }))
      .filter((row) => row.values.length > 0),
    title: section.title,
  })).filter((section) => section.rows.length > 0);
}

export interface RepoSnippetLineRangeView {
  startLine: number;
  endLine: number;
  codeSnippet: string;
}

export interface RepoSnippetReferenceView {
  id: string;
  path: string;
  pathKind: "file" | "directory";
  whyItMatters: string;
  reusableElements: string[];
  lineRanges: RepoSnippetLineRangeView[];
}

export interface RepoSnippetsView {
  summary: string;
  references: RepoSnippetReferenceView[];
}

function toPositiveInteger(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string") {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
}

export function readRepoSnippets(
  metadata: Record<string, unknown> | null,
): RepoSnippetsView | null {
  if (!metadata) {
    return null;
  }
  const candidate = metadata.repo_snippets ?? metadata.context_snippets;
  if (!isRecord(candidate)) {
    return null;
  }

  const referencesRaw = Array.isArray(candidate.references)
    ? candidate.references
    : [];
  const references = referencesRaw
    .map((referenceRaw, index): RepoSnippetReferenceView | null => {
      if (!isRecord(referenceRaw)) {
        return null;
      }
      const path = toStringValue(referenceRaw.path);
      if (!path) {
        return null;
      }
      const pathKind =
        referenceRaw.path_kind === "directory" ? "directory" : "file";
      const whyItMatters =
        toStringValue(referenceRaw.why_it_matters) ??
        "High-signal repository context.";
      const reusableElements = Array.isArray(referenceRaw.reusable_elements)
        ? dedupe(
            referenceRaw.reusable_elements
              .map((entry) => toStringValue(entry))
              .filter((entry): entry is string => Boolean(entry)),
          )
        : [];
      const lineRangesRaw = Array.isArray(referenceRaw.line_ranges)
        ? referenceRaw.line_ranges
        : [];
      const lineRanges = lineRangesRaw
        .map((lineRangeRaw): RepoSnippetLineRangeView | null => {
          if (!isRecord(lineRangeRaw)) {
            return null;
          }
          const startLine = toPositiveInteger(lineRangeRaw.start_line);
          const endLine = toPositiveInteger(lineRangeRaw.end_line);
          if (!startLine || !endLine) {
            return null;
          }
          return {
            codeSnippet: toStringValue(lineRangeRaw.code_snippet) ?? "",
            endLine: Math.max(startLine, endLine),
            startLine,
          };
        })
        .filter((lineRange): lineRange is RepoSnippetLineRangeView =>
          Boolean(lineRange),
        );

      return {
        id: `${path}-${index}`,
        lineRanges,
        path,
        pathKind,
        reusableElements,
        whyItMatters,
      };
    })
    .filter((reference): reference is RepoSnippetReferenceView =>
      Boolean(reference),
    );

  if (references.length === 0) {
    return null;
  }

  const summary =
    toStringValue(candidate.summary) ??
    "Indexed repository snippet references.";
  return { references, summary };
}
